import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {Input, ListScreen, Select} from '../components';
import {
    fetchFiles,
    cleanFiles,
    setUser,
    changeUserSetting,
    deleteFile,
} from '../actions';
import {__, formatDate, isEmptyString, request, toNumber, getUrl} from '../functions';
import '../assets/styles/files.css';
import { FILES_NAMES } from '../config';
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from '@mui/icons-material/Delete';
import {ButtonBase} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/AddCircle";

/**
 * Files.
 */
class FilesScreen extends ListScreen {
    title = __('Úložisko');

    state = { ...ListScreen.state, ...{
        lightbox: {
            delete: false,
            create: false,
            edit: false,
        },
    }};

    colors = [
        "#E74C3C",
        "#E67E22",
        "#F1C40F",
        "#2ECC71",
        "#1ABC9C",
        "#3498DB",
        "#3F51B5",
        "#9B59B6",
        "#E91E63",
        "#8D6E63",
        "#95A5A6",
        "#7F8C8D",
        "#CDDC39",
        "#00BCD4",
        "#FFC107",
        "#FF5722",
        "#03A9F4",
        "#8BC34A",
        "#673AB7",
        "#607D8B"
    ];

    memoryCacheCategories = {};

    isValidPermission() {
        return this.hasPermission('files');
    }

    getTableName() {
        return 'files';
    }

    getFilter() {
        const { items } = this.props;

        return _.reduce(items.filter, (result, options, key) => {
            switch (key) {
                case 'name':
                    return { ...result, ...{ [key]: {
                        type: 'input',
                        name: __('Názov'),
                        value: '',
                    }}};

                case 'file_category_id':
                    return { ...result, ...{ [key]: {
                        type: 'select',
                        name: __('Kategória'),
                        value: '',
                        options,
                    }}};

                default:
                    return result;
            }
        }, {});
    }

    getColumns() {
        return [
            <div>{__('Názov')}</div>,
            <div>{__('Typ')}</div>,
            <div>{__('Prípona')}</div>,
            <div>{__('Vytvorené')}</div>,
            <div>{__('Posledná zmena')}</div>,
            <div></div>,
        ];
    }

    getCells(item) {
        const { items } = this.props;

        const categories = _.reduce(
            items.categories,
            (result, { id, name, color }) => ({ ...result, [id]: { name, color } }),
            {}
        );

        let category = null;

        if (toNumber(item.file_category_id) > 0 && _.has(categories, item.file_category_id)) {
            category = <div className="category" style={{ backgroundColor: categories[item.file_category_id].color }}>
                {categories[item.file_category_id].name}
            </div>;
        }

        const isDefault = toNumber(item.def) > 0;

        if (isDefault) {
            category = <div className="category" style={{ backgroundColor: '#c8c4c4' }}>{__('System')}</div>;
        }

        const name = isDefault && _.has(FILES_NAMES, item.name) ? FILES_NAMES[item.name] : item.name;

        return [
            <div>
                <div>{_.truncate(name, { length: 32 })}</div>
                {category}
            </div>,
            <div>{_.has(item, 'link_url')
                ? <a href={item.link_url} target="_blank">{_.truncate(item.link_url, { length: 32 })}</a>
                : __('Súbor')}</div>,
            <div>{item.extension}</div>,
            <div>{formatDate(item.created, 'dd.mm.yyyy hh:ii')}</div>,
            <div>{formatDate(item.modified, 'dd.mm.yyyy hh:ii')}</div>,
            <div>
                {this.hasPermission('files-download') ? this.renderTableButton(
                    item.id,
                    __('Stiahnuť'),
                    <DownloadIcon />,
                    (callbackLoading, callback) => this.download(item.id, callbackLoading, callback),
                    {}
                ) : null}
                {this.hasPermission('files-upload') && !_.has(item, 'link_url') ? this.renderTableButton(
                    item.id,
                    __('Upraviť'),
                    <EditIcon />,
                    (callbackLoading, callback) => this.confirmEdit(item.name, callbackLoading, callback),
                    {}
                ) : null}
                {this.hasPermission('files-delete') ? this.renderTableButton(
                    item.id,
                    __('Zmazať'),
                    <DeleteIcon />,
                    (callbackLoading, callback) => this.confirmDelete(item.id, callbackLoading, callback),
                    { disabled: isDefault }
                ) : null}
            </div>,
        ];
    }

    getTags() {
        const { user } = this.props;

        if (!this.hasPermission('files-upload')) {
            return [];
        }

        return [
            {
                name: __('Nový súbor'),
                onClick: () => this.showLightbox('create', {
                    loading: false,
                    data: {
                        type: 'file',
                        category_id: 0,
                        name: '',
                        file: null,
                        url: '',
                        create_category: false,
                        create_category_name: '',
                        create_category_color: '',
                    },
                }),
                color: 'secondary',
                icon: <AddIcon />,
                disabled: this.isDemo() || toNumber(user.user_eshop_id) === 0,
            },
            {
                name: __('Nový súbor z url'),
                onClick: () => this.showLightbox('create', {
                    loading: false,
                    data: {
                        type: 'link',
                        category_id: 0,
                        name: '',
                        file: null,
                        url: '',
                        create_category: false,
                        create_category_name: '',
                        create_category_color: '',
                    },
                }),
                color: 'secondary',
                icon: <AddIcon />,
                disabled: this.isDemo() || toNumber(user.user_eshop_id) === 0,
            },
        ];
    }

    getEmptyText() {
        return __('Zatiaľ nemáte žiadne súbory');
    }

    onChangeCreate(key, value) {
        const { lightbox } = this.state;

        let additional = {};

        if (key === 'file' && typeof value.name !== 'undefined' && isEmptyString(lightbox.create.data.name)) {
            additional = { name: value.name.toString().replace(/\.[^/.]+$/, '') };
        }

        this.setState({ lightbox: { ...lightbox, create: {
            ...lightbox.create,
            data: { ...lightbox.create.data, [key]: value, ...additional }
        } } });
    }

    onChangeCreateLoading(value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, create: { ...lightbox.create, loading: value } } })
    }

    confirmEdit(name, callbackLoading, callback) {
        this.showLightbox('edit', {
            data: {
                name: name,
                file: null,
            },
            callbackLoading,
            callback,
        });
    }

    onChangeEdit(key, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, edit: {
            ...lightbox.edit,
            data: { ...lightbox.edit.data, [key]: value }
        } } });
    }

    confirmDelete(id, callbackLoading, callback) {
        this.showLightbox('delete', { id, callbackLoading, callback });
    }

    async edit() {
        const { lightbox } = this.state;

        if (lightbox.edit.data.file === null) {
            this.showSnackbar('error', __('Nie je nahratý súbor'));
            return;
        }

        // Zavolame loading
        lightbox.edit.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('edit');

        await request('/files/edit', lightbox.edit.data.file, 'FILE', {
            file_name: lightbox.edit.data.name,
        }).then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', __('Súbor sa nepodarilo nahrať'));
                return;
            }

            this.showSnackbar('success', __('Súbor bol zmenený'));
        });

        // Zavolame callback
        lightbox.edit.callback();
    }

    async create() {
        const { lightbox } = this.state;

        if (isEmptyString(lightbox.create.data.name)) {
            this.showSnackbar('error', __('Nie je vyplnený názov'));
            return;
        } else if (lightbox.create.data.type === 'file' && lightbox.create.data.file === null) {
            this.showSnackbar('error', __('Nie je nahratý súbor'));
            return;
        } else if (lightbox.create.data.type === 'link' && isEmptyString(lightbox.create.data.url)) {
            this.showSnackbar('error', __('Nie je nahratý súbor'));
            return;
        }

        this.onChangeCreateLoading(true);

        let file_category_id = lightbox.create.data.category_id;

        if (lightbox.create.data.create_category && !isEmptyString(lightbox.create.data.create_category_name)) {
            await request('/file-categories/create', {
                name: lightbox.create.data.create_category_name,
                color: lightbox.create.data.create_category_color,
            }, 'POST').then(response => {
                const { status, data } = response.data;

                if (status === 'error') {
                    return;
                }

                if (_.has(data, 'id')) {
                    file_category_id = data.id;
                }
            });
        }

        if (lightbox.create.data.type === 'link') {
            request('/links/create', {
                url: lightbox.create.data.url,
                file_name: lightbox.create.data.name,
                file_category_id,
            }, 'POST').then(response => {
                const { status } = response.data;

                if (status === 'error') {
                    this.onChangeCreateLoading(false);
                    this.showSnackbar('error', __('Súbor sa nepodarilo nahrať'));
                    return;
                }

                // Refreshneme
                window.location = window.location.pathname;
            });

            return;
        }

        request('/files/upload', lightbox.create.data.file, 'FILE', {
            file_name: lightbox.create.data.name,
            file_category_id,
        }).then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.onChangeCreateLoading(false);
                this.showSnackbar('error', __('Súbor sa nepodarilo nahrať'));
                return;
            }

            // Refreshneme
            window.location = window.location.pathname;
        });
    }

    async download(id, callbackLoading, callback) {
        callbackLoading();

        window.location = `${getUrl()}/files/download/${id}?token=${!_.isUndefined(global.token) ? global.token : ''}`;

        callback();
    }

    async delete() {
        const { deleteFile, items } = this.props;
        const { lightbox } = this.state;

        // Zavolame loading
        lightbox.delete.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('delete');

        // Zmazeme
        await deleteFile(this, lightbox.delete.id);

        // Zavolame callback
        lightbox.delete.callback();

        // Znovu nacitame zoznam od prvej stranky
        this.fetchList(1, 0, items.filtered);
    }

    renderScreen() {
        const { items } = this.props;
        const { lightbox } = this.state;

        const categoryNames = _.reduce(
            items.categories,
            (result, { id, name }) => ({ ...result, [id]: name }),
            {}
        );

        const categoryColors = _.reduce(
            items.categories,
            (result, { id, color }) => ({ ...result, [id]: color }),
            {}
        );

        return (
            <div>
                {this.renderLightbox(
                    'delete',
                    __('Naozaj chcete zmazať tento súbor?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.delete()
                )}
                {this.renderLightbox(
                    'create',
                    __('Nový súbor'),
                    !_.isEmpty(lightbox.create) ? (lightbox.create.loading ? this.renderLoading(20) : <div className="create-file">
                        {lightbox.create.data.type === 'file' ? <div className="create-file__upload-text">{__('Súbor')}</div> : null}
                        {lightbox.create.data.type === 'file' ? <ButtonBase className="create-file__upload">
                            <div className="create-file__upload__text">{lightbox.create.data.file !== null
                                ? lightbox.create.data.file.name
                                : __('Kliknite alebo presuňte súbor (pdf, doc, xls, xml, csv, txt, max. 10MB)')}</div>
                            <input type="file" onChange={event => this.onChangeCreate('file', event.target.files[0])} />
                        </ButtonBase> : null}
                        {lightbox.create.data.type === 'link' ? <Input
                            label={__('Url adresa')}
                            value={lightbox.create.data.url}
                            onChange={value => this.onChangeCreate('url', value)}
                        /> : null}
                        <div>
                            <Input
                                label={__('Názov')}
                                value={lightbox.create.data.name}
                                onChange={value => this.onChangeCreate('name', value)}
                            />
                            {!lightbox.create.data.create_category ? <div>
                                <div className="create-file__category">
                                    <Select
                                        label={__('Kategória')}
                                        value={lightbox.create.data.category_id}
                                        options={categoryNames}
                                        onChange={value => this.onChangeCreate('category_id', value)}
                                    />
                                    <div
                                        className="create-file__category__color"
                                        style={toNumber(lightbox.create.data.category_id) > 0
                                            ? { backgroundColor: categoryColors[lightbox.create.data.category_id] }
                                            : {}}
                                    />
                                </div>
                                <ButtonBase
                                    onClick={() => this.onChangeCreate('create_category', true)}
                                    className="create-file__category-button"
                                >{__('Vytvoriť novú kategóriu')}</ButtonBase>
                            </div> : <div className="create-file__category-create">
                                <Input
                                    label={__('Názov kategórie')}
                                    value={lightbox.create.data.create_category_name}
                                    onChange={value => this.onChangeCreate('create_category_name', value)}
                                />
                                <div className="create-file__category-create__colors">
                                    <div className="create-file__category-create__colors__title">{__('Farba kategórie')}</div>
                                    <div className="create-file__category-create__colors__items">
                                        {_.map(this.colors, color => {
                                            return (
                                                <ButtonBase
                                                    onClick={() => this.onChangeCreate('create_category_color', color)}
                                                    className={lightbox.create.data.create_category_color === color ? 'active' : ''}
                                                >
                                                    <div style={{ backgroundColor: color }} />
                                                </ButtonBase>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>}
                        </div>
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.create()
                )}
                {this.renderLightbox(
                    'edit',
                    __('Úprava súboru'),
                    !_.isEmpty(lightbox.edit) ? (<div className="create-file">
                        <div className="create-file__upload-text">{__('Súbor')}</div>
                        <ButtonBase className="create-file__upload">
                            <div className="create-file__upload__text">{lightbox.edit.data.file !== null
                                ? lightbox.edit.data.file.name
                                : __('Kliknite alebo presuňte súbor (pdf, doc, xls, xml, csv, txt, max. 10MB)')}</div>
                            <input type="file" onChange={event => this.onChangeEdit('file', event.target.files[0])} />
                        </ButtonBase>
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.edit()
                )}
            </div>
        );
    }
}

const stateToProps = ({ files, user }) => ({ items: files, user });

export default withCookies(connect(stateToProps, {
    fetch: fetchFiles,
    clean: cleanFiles,
    setUser,
    changeUserSetting,
    deleteFile,
})(FilesScreen));
